import { createElement, type ReactNode } from 'react';
import {
  ClipboardTextIcon,
  CurrencyDollarIcon,
  EnvelopeSimpleIcon,
  FilesIcon,
  InvoiceIcon,
  ListChecksIcon,
  RobotIcon,
  TrayIcon,
  TruckIcon,
  UsersIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { TaskPageIconKey } from '@erp/shared-types';

/**
 * The fixed icon vocabulary for task pages (`TASK_PAGE_ICON_KEYS` in
 * @erp/shared-types). Unknown or null keys fall back to the list icon so a
 * key removed from the set can never break rendering.
 */
export const TASK_PAGE_ICON_COMPONENTS: Record<TaskPageIconKey, Icon> = {
  'list-checks': ListChecksIcon,
  envelope: EnvelopeSimpleIcon,
  invoice: InvoiceIcon,
  tray: TrayIcon,
  clipboard: ClipboardTextIcon,
  files: FilesIcon,
  truck: TruckIcon,
  'currency-dollar': CurrencyDollarIcon,
  users: UsersIcon,
  robot: RobotIcon,
};

const DEFAULT_ICON = ListChecksIcon;

/**
 * Own-property narrowing: `in` would also match inherited keys such as
 * `toString`/`constructor`, resolving to non-component values instead of the
 * fallback icon.
 */
function isTaskPageIconKey(iconKey: string): iconKey is TaskPageIconKey {
  return Object.prototype.hasOwnProperty.call(
    TASK_PAGE_ICON_COMPONENTS,
    iconKey,
  );
}

export function taskPageIconComponent(iconKey: string | null): Icon {
  if (iconKey !== null && isTaskPageIconKey(iconKey)) {
    return TASK_PAGE_ICON_COMPONENTS[iconKey];
  }
  return DEFAULT_ICON;
}

/** Sidebar-sized icon node for a task page. */
export function taskPageNavIcon(iconKey: string | null): ReactNode {
  const IconComponent = taskPageIconComponent(iconKey);
  return <IconComponent className="h-4 w-4" />;
}

/**
 * The page's icon as a component, so callers render `<PageIcon iconKey=… />`.
 * Built with `createElement` on the resolved type rather than a local
 * `<IconComponent />` tag, which the React Compiler reads as creating a new
 * component on every render.
 */
export function PageIcon({
  iconKey,
  className,
}: {
  iconKey: string | null;
  className?: string;
}) {
  return createElement(taskPageIconComponent(iconKey), { className });
}
